import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/client';
import { CODE_HINT_KEY } from '@/lib/aiPipeline';
import { apiErrorCode } from '@/lib/apiErrorCode';
import { humanizeError } from '@/lib/errors';
import { cn } from '@/lib/utils';

export type Note = { tone: 'ok' | 'hint'; text: string } | null;

/**
 * Отказ сервера. Причину различаем только по `code` (договор §0): подсказка из
 * словаря, а мелкой строкой — `HTTP · code`, чтобы проверяющая могла назвать отказ.
 */
export function ApiErrorText({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = apiErrorCode(error);
  const status = typeof (error as ApiError)?.status === 'number' ? (error as ApiError).status : 0;
  const hint =
    code && CODE_HINT_KEY[code]
      ? t(CODE_HINT_KEY[code])
      : status === 403
        ? t('adminAiPipeline.error.forbidden')
        : humanizeError(error);
  const tech = [status ? `HTTP ${status}` : '', code ?? ''].filter(Boolean).join(' · ');
  return (
    <div data-ai-error role="alert" className="rounded-xl bg-error-container px-3 py-2 text-sm text-on-error-container">
      <div>{hint}</div>
      {tech ? <div className="mt-0.5 font-mono text-xs opacity-80">{tech}</div> : null}
    </div>
  );
}

export function NoteLine({ note }: { note: Note }) {
  if (!note) return null;
  return (
    <div
      data-ai-note={note.tone}
      className={cn(
        'rounded-xl px-3 py-2 text-sm',
        note.tone === 'ok' ? 'bg-success-container text-on-success-container' : 'bg-warning-container text-on-warning-container',
      )}
    >
      {note.text}
    </div>
  );
}
